/**
 * Pure validation functions for checkout flow
 * These can be unit tested without React dependencies
 */

import { ValidationResult, Product, GuestAddress } from './checkout-types';

/** Shown when no carrier can price this parcel, so it cannot be bought online. */
export const SHIPPING_UNAVAILABLE_MESSAGE = 'This seller cannot ship to this address yet, so it cannot be bought online. Message the seller to arrange it.';

/**
 * Shown when the quote for this address failed or found no rate. Not "select a
 * shipping method": there is nothing on the page to select.
 */
export const SHIPPING_QUOTE_FAILED_MESSAGE = 'We could not get shipping options for this address. Check the address, or press Try again under Shipping Method.';

/** Shown for a listing its seller marked not shippable. Every order ships, so it cannot check out. */
export const PICKUP_ONLY_MESSAGE = 'This item is pickup only, so it cannot be bought online. Message the seller to arrange it.';

/**
 * Validate product has required fields for checkout
 */
export function validateProductForCheckout(product: Product | null | undefined): ValidationResult {
  if (!product?.id || !product?.title || product?.price === undefined || product?.price === null) {
    return { valid: false, error: 'Product information is incomplete' };
  }
  return { valid: true };
}

/**
 * Validate product has minimum required fields (less strict)
 */
export function validateProductBasic(product: Product | null | undefined): ValidationResult {
  if (!product?.id) {
    return { valid: false, error: 'Product information is missing' };
  }
  return { valid: true };
}

/**
 * Validate seller has completed Stripe payout setup
 * trustLevel must be >= 2 for checkout to be available
 */
export function validateSellerTrustLevel(product: Product | null | undefined): ValidationResult {
  const trustLevel = product?.user?.trustLevel ? Number(product.user.trustLevel) : 0;
  if (trustLevel < 2) {
    return {
      valid: false,
      error: 'This seller has not completed their payout account setup. Checkout is not available.'
    };
  }
  return { valid: true };
}

/**
 * Validate the shipping selection. Every listing that checks out ships, so
 * every one needs a rate to pay for (owner, 2026-10-08: "we ship all checked
 * out listings").
 *
 * Vehicles, non-shippable listings and "arranged separately" quotes used to
 * pass with no rate and were charged $0 shipping. A Hot Wheels car filed in
 * Vehicles > Cars checked out that way, and so did every Vehicles > Parts
 * listing. We never check out a real vehicle: those are dealer feeds.
 */
export function validateShippingSelection(
  product: Product | null | undefined,
  shippingIndex: number | null | undefined,
  /**
   * The quote came back saying no carrier can price this parcel: a
   * cross-border route, or a seller with no origin to quote from. Asserted by
   * the server, never inferred from an empty list. It blocks with its own
   * message, because "select a shipping method" from an empty list tells the
   * buyer nothing.
   */
  arrangedSeparately = false,
  /**
   * How many rates the quote returned. The index has to point at one of them.
   *
   * Checking the index alone let an empty list through at index 0 -- the
   * default -- so a quote that failed sent `shippingProviders[0]`, which is
   * undefined, and the order went through charging nothing for a parcel that
   * still needed a label.
   */
  rateCount = 0,
  /** The quote for this address failed or found nothing (CheckoutProvider shippingQuoteFailed). */
  quoteFailed = false
): ValidationResult {
  if (!product) {
    return { valid: false, error: 'Please select a shipping method' };
  }

  if (!isProductShippable(product)) {
    return { valid: false, error: PICKUP_ONLY_MESSAGE };
  }

  if (arrangedSeparately) {
    return { valid: false, error: SHIPPING_UNAVAILABLE_MESSAGE };
  }

  if (quoteFailed && rateCount === 0) {
    return { valid: false, error: SHIPPING_QUOTE_FAILED_MESSAGE };
  }

  // Every listing needs a valid shipping selection
  if (
    shippingIndex === null ||
    shippingIndex === undefined ||
    shippingIndex < 0 ||
    shippingIndex >= rateCount
  ) {
    return { valid: false, error: 'Please select a shipping method' };
  }

  return { valid: true };
}

/**
 * Validate payment method is selected (for authenticated checkout)
 */
export function validatePaymentMethod(paymentMethodId: string | null | undefined): ValidationResult {
  if (!paymentMethodId) {
    return { valid: false, error: 'No payment method selected' };
  }
  return { valid: true };
}

/**
 * Validate guest address has all required fields
 */
export function validateGuestAddress(address: GuestAddress | null | undefined): ValidationResult {
  if (!address) {
    return { valid: false, error: 'Shipping address is required' };
  }

  const { line1, city, state, postal_code } = address;

  if (!line1 || !city || !state || !postal_code) {
    return {
      valid: false,
      error: 'Shipping address is incomplete',
      // Include details for logging
    };
  }

  return { valid: true };
}

/**
 * Get missing fields from guest address (for logging)
 */
export function getGuestAddressMissingFields(address: GuestAddress | null | undefined): Record<string, string> {
  if (!address) {
    return { line1: '', city: '', state: '', postal_code: '' };
  }
  return {
    line1: address.line1 || '',
    city: address.city || '',
    state: address.state || '',
    postal_code: address.postal_code || '',
  };
}

/**
 * Validate guest email
 */
export function validateGuestEmail(email: string | null | undefined): ValidationResult {
  if (!email || !email.trim()) {
    return { valid: false, error: 'Email is required' };
  }

  // Basic email format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Invalid email format' };
  }

  return { valid: true };
}

/**
 * Check if product is shippable
 */
export function isProductShippable(product: Product | null | undefined): boolean {
  return product?.shippable === 1 || product?.shippable === true || product?.shippable === '1';
}
